import { useState, useEffect } from 'react';
import {
  useSendEmailMutation,
  useVerifyEmailCodeMutation,
  useSendEmailForPasswordMutation,
  useSendLoginEmailCodeMutation,
} from '@features/auth/hooks';

export function useFindAccount({
  mode,
  onSuccess,
  onError,
  onResendSuccess,
  loginUserId,
  loginCompanyCode,
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loginId, setLoginId] = useState('');
  const [code, setCode] = useState('');
  const [companyCode, setCompanyCode] = useState('');

  const [expireAt, setExpireAt] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [requestId, setRequestId] = useState(null);
  const [isCodeSent, setIsCodeSent] = useState(false);

  const [resendAvailableAt, setResendAvailableAt] = useState(null);
  const [resendRemaining, setResendRemaining] = useState(0);

  /* =========================
   * 공통 에러 처리
   ========================= */
  const notifyError = (error, fallback) => {
    const message =
      error?.response?.data?.error_msg ??
      error?.response?.data?.message ??
      error?.error_msg ??
      error?.message ??
      fallback;

    onError?.(message);
  };

  /* =========================
   * 인증 메일 성공 처리
   ========================= */
  const handleSendEmailSuccess = (data, { isResend } = {}) => {
    if (data?.result_code !== 0) {
      notifyError(data, '인증 메일 발송에 실패했습니다.');
      return;
    }

    const resultData = data?.resultData;
    if (!resultData) {
      onError?.('서버 응답을 처리할 수 없습니다.');
      return;
    }

    const serverExpire = new Date(resultData.expireAt);
    setExpireAt(serverExpire);
    setRequestId(resultData.requestId);
    setRemaining(Math.max(0, Math.floor((serverExpire.getTime() - Date.now()) / 1000)));

    setResendAvailableAt(Date.now() + 60 * 1000);
    setResendRemaining(60);
    setIsCodeSent(true);

    if (isResend) onResendSuccess?.();
  };

  /* =========================
   * API Hooks (최상단 고정)
   ========================= */
  const { mutate: sendLoginEmail } = useSendLoginEmailCodeMutation({
    onSuccess: (data) => handleSendEmailSuccess(data, { isResend: isCodeSent }),
    onError: (error) => notifyError(error, '서버 통신 중 오류가 발생했습니다.'),
  });

  const { mutate: sendEmailForId, isPending: isSendingId } = useSendEmailMutation({
    onSuccess: (data) => handleSendEmailSuccess(data, { isResend: isCodeSent }),
    onError: (error) => notifyError(error, '서버 통신 중 오류가 발생했습니다.'),
  });

  const { mutate: sendEmailForPw, isPending: isSendingPw } = useSendEmailForPasswordMutation({
    onSuccess: (data) => handleSendEmailSuccess(data, { isResend: isCodeSent }),
    onError: (error) => notifyError(error, '서버 통신 중 오류가 발생했습니다.'),
  });

  const { mutate: verifyCode, isPending: isVerifying } = useVerifyEmailCodeMutation({
    onSuccess(data) {
      if (data?.result_code !== 0) {
        notifyError(data, '인증에 실패했습니다.');
        return;
      }
      onSuccess?.({ mode, result: data, email, requestId, emailCode: code });
    },
    onError: (error) => notifyError(error, '인증에 실패했습니다.'),
  });

  /* =========================
   * 타이머
   ========================= */
  useEffect(() => {
    if (!expireAt) return;
    const timer = setInterval(() => {
      const sec = Math.floor((expireAt.getTime() - Date.now()) / 1000);
      setRemaining(sec > 0 ? sec : 0);
      if (sec <= 0) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [expireAt]);

  useEffect(() => {
    if (!resendAvailableAt) return;
    const timer = setInterval(() => {
      const sec = Math.floor((resendAvailableAt - Date.now()) / 1000);
      setResendRemaining(sec > 0 ? sec : 0);
      if (sec <= 0) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendAvailableAt]);

  /* =========================
   * 액션
   ========================= */
  const handleSendCode = () => {
    if (isCodeSent && resendRemaining > 0) return;

    //  로그인 이메일 인증
    if (mode === 'emailVerification') {
      if (!loginUserId || !loginCompanyCode) {
        onError?.('로그인 정보가 없습니다. 다시 로그인해주세요.');
        return;
      }

      if (!email) {
        onError?.('이메일을 입력해주세요.');
        return;
      }

      sendLoginEmail({
        userId: loginUserId,
        companyCode: loginCompanyCode,
        email,
      });
      return;
    }
    if (!name || !email) {
      onError?.('이름과 이메일을 입력해 주세요.');
      return;
    }

    if (mode === 'findPassword') {
      if (!loginId) {
        onError?.('로그인 아이디를 입력해 주세요.');
        return;
      }
      sendEmailForPw({ name, email, id: loginId, companyCode });
    } else {
      sendEmailForId({ name, email });
    }
  };

  const handleVerify = () => {
    if (!code || !requestId) {
      onError?.('인증번호를 다시 확인해주세요.');
      return;
    }

    verifyCode({
      name,
      email,
      code,
      requestId,
      purpose:
        mode === 'findPassword'
          ? 'FIND_PASSWORD'
          : mode === 'emailVerification'
            ? 'LOGIN'
            : 'FIND_ID',
    });
  };

  const formatTime = (sec) =>
    `${String(Math.floor(sec / 60)).padStart(2, '0')}:${String(sec % 60).padStart(2, '0')}`;

  const reset = () => {
    setName('');
    setEmail('');
    setLoginId('');
    setCode('');
    setCompanyCode('');
    setExpireAt(null);
    setRemaining(0);
    setRequestId(null);
    setIsCodeSent(false);
    setResendAvailableAt(null);
    setResendRemaining(0);
  };

  return {
    state: {
      name,
      email,
      loginId,
      code,
      remaining,
      resendRemaining,
      isCodeSent,
      isSending: isSendingId || isSendingPw,
      isVerifying,
      companyCode,
      requestId,
    },
    actions: {
      setName,
      setEmail,
      setLoginId,
      setCode,
      setCompanyCode,
      handleSendCode,
      handleVerify,
      formatTime,
      reset,
    },
  };
}
